// ui-kit: hand-built
// Ported from platform/app/src/components/new/Box.tsx (not from Figma).
import {
    Children,
    Fragment,
    isValidElement,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
    type Ref,
} from 'react';
import { RiArrowDownSLine, RiArrowUpSLine, RiInformationLine } from '@remixicon/react';

import { classNames } from '../../internal/classNames';
import { isSlot } from '../../internal/slot';

import Button, { ButtonSizeContext } from '../Button/Button';
import Divider from '../Divider/Divider';
import IconAction from '../IconAction/IconAction';

type DataTestAttributes = {
    'data-test-component'?: string;
    'data-test-id'?: string;
    'data-test-name'?: string;
};

const COLLAPSE_TRANSITION_MS = 300;

export type BoxHandler = {
    expand: (state: boolean) => Promise<void>;
};

export type BoxProps = {
    reverseHeader?: boolean;
    fullWidthContent?: boolean;
    className?: string;
    children?: ReactNode;
    collapsible?: boolean;
    initCollapsed?: boolean;
    ref?: Ref<BoxHandler>;
} & DataTestAttributes;

export type BoxTitleProps = {
    extra?: string;
    children?: ReactNode;
    className?: string;
    info?: string;
};

export type BoxActionsProps = {
    children?: ReactNode;
};

export type BoxFiltersProps = {
    children?: ReactNode;
};

export type BoxAddOnProps = {
    children?: ReactNode;
    align?: 'start' | 'center' | 'end';
    direction?: 'row' | 'column';
    divided?: boolean | 'top' | 'bottom';
};

export type BoxFooterProps = {
    children?: ReactNode;
    divided?: boolean;
    className?: string;
};

export type BoxOverlineProps = {
    children?: ReactNode;
};

function Box({
    reverseHeader = false,
    fullWidthContent = false,
    className = '',
    children,
    collapsible = false,
    initCollapsed = false,
    'data-test-component': testComponent = 'ui-kit/Box',
    'data-test-id': testId,
    'data-test-name': testName,
    ref,
}: BoxProps) {
    const titleElements: ReactNode[] = [];
    const actionElements: ReactNode[] = [];
    const filterElements: ReactNode[] = [];
    const contentElements: ReactNode[] = [];
    const footerElements: ReactNode[] = [];
    const overlineElements: ReactNode[] = [];

    const extractChild = (child: ReactNode) => {
        if (!isValidElement(child)) {
            contentElements.push(child);
            return;
        }

        if (child.type === Fragment) {
            Children.forEach(
                (child as ReactElement<{ children?: ReactNode }>).props.children,
                extractChild,
            );
            return;
        }

        if (isSlot(child, Title.slot)) {
            titleElements.push(child);
            return;
        }
        if (isSlot(child, Actions.slot)) {
            actionElements.push(child);
            return;
        }
        if (isSlot(child, Filters.slot)) {
            filterElements.push(child);
            return;
        }
        if (isSlot(child, Footer.slot)) {
            footerElements.push(child);
            return;
        }
        if (isSlot(child, Overline.slot)) {
            overlineElements.push(child);
            return;
        }

        // Box.AddOn and any other node land in the content area.
        contentElements.push(child);
    };

    Children.forEach(children, extractChild);

    const showActionsAndFilters =
        actionElements.length > 0 || filterElements.length > 0 || collapsible;
    const showDivider =
        actionElements.length > 0 && filterElements.length > 0 && titleElements.length > 0;
    const showHeader = titleElements.length > 0 || showActionsAndFilters;

    const [expanded, setExpanded] = useState(!initCollapsed);
    const [transitionsReady, setTransitionsReady] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Avoid animating the initial collapsed/expanded paint.
        setTransitionsReady(true);
    }, []);

    const expand = (state: boolean) =>
        new Promise<void>((resolve) => {
            if (state === expanded) {
                resolve();
                return;
            }

            const el = bodyRef.current;
            if (!collapsible || !el || !transitionsReady) {
                setExpanded(state);
                resolve();
                return;
            }

            let settled = false;
            const settle = () => {
                if (settled) return;
                settled = true;
                el.removeEventListener('transitionend', onEnd);
                resolve();
            };

            const onEnd = (event: TransitionEvent) => {
                if (event.target !== el) return;
                if (
                    event.propertyName !== 'grid-template-rows' &&
                    event.propertyName !== 'margin-top' &&
                    event.propertyName !== 'margin-bottom'
                ) {
                    return;
                }
                settle();
            };

            el.addEventListener('transitionend', onEnd);
            setExpanded(state);
            window.setTimeout(settle, COLLAPSE_TRANSITION_MS + 50);
        });

    useImperativeHandle(ref, () => ({ expand }));

    return (
        <div
            className={classNames({
                'ui-box-wrapper': true,
                'ui-box-wrapper--with-overline': overlineElements.length > 0,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-id={testId}
            data-test-name={testName}>
            {overlineElements.length > 0 && (
                <div className='ui-box__overline-wrapper'>{overlineElements}</div>
            )}
            <div
                className={classNames({
                    'ui-box': true,
                    'ui-box--collapsed': collapsible && !expanded,
                })}>
                {showHeader && (
                    <div
                        className={classNames({
                            'ui-box__header': true,
                            'ui-box__header--reverse': reverseHeader,
                            'ui-box__header--collapsed': collapsible && !expanded,
                        })}
                        data-test-component='ui-kit/Box/Header'>
                        {titleElements}
                        {showActionsAndFilters && (
                            <div
                                className={classNames({
                                    'ui-box__header-tools': true,
                                    'ui-box__header-tools--expands': titleElements.length === 0,
                                })}
                                data-test-component='ui-kit/Box/Header/ActionsAndFilters'>
                                {filterElements}
                                {showDivider && <Divider orientation='Vertical' />}
                                {actionElements}
                                {collapsible && (
                                    <Button
                                        size='medium'
                                        variant='filled-light'
                                        onClick={() => {
                                            void expand(!expanded);
                                        }}
                                        icon={expanded ? RiArrowUpSLine : RiArrowDownSLine}
                                        title={expanded ? 'Collapse' : 'Expand'}
                                        data-test-component='ui-kit/Box/Header/ActionsAndFilters/ShowHide'
                                        data-test-name={expanded ? 'hide' : 'show'}
                                        name='collapse-handler'
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div
                    ref={bodyRef}
                    inert={collapsible ? !expanded : undefined}
                    className={classNames({
                        'ui-box__content': true,
                        'ui-box__content--headerless': !showHeader,
                        'ui-box__content--full-width': fullWidthContent,
                        'ui-box__content--collapsible': collapsible && transitionsReady,
                        'ui-box__content--collapsed': collapsible && !expanded,
                    })}>
                    <div className='ui-box__content-inner'>{contentElements}</div>
                </div>

                {footerElements}
            </div>
        </div>
    );
}

function Title({ extra, children, className = '', info }: BoxTitleProps) {
    return (
        <div
            className={classNames({
                'ui-box__title': true,
                [className]: Boolean(className),
            })}>
            <div className='ui-box__title-text'>{children}</div>
            {extra ? <span className='ui-box__title-extra'>{extra}</span> : null}
            {info ? (
                <IconAction
                    icon={RiInformationLine}
                    title={info}
                    aria-label={info}
                    className='ui-box__title-info'
                />
            ) : null}
        </div>
    );
}

function Actions({ children }: BoxActionsProps) {
    return (
        <div className='ui-box__actions'>
            <ButtonSizeContext.Provider value='medium'>{children}</ButtonSizeContext.Provider>
        </div>
    );
}

function Filters({ children }: BoxFiltersProps) {
    return (
        <div className='ui-box__filters'>
            <ButtonSizeContext.Provider value='medium'>{children}</ButtonSizeContext.Provider>
        </div>
    );
}

function AddOn({
    children,
    align = 'center',
    direction = 'column',
    divided = true,
}: BoxAddOnProps) {
    return (
        <div
            className={classNames({
                'ui-box__addon': true,
                [`ui-box__addon--align-${align}`]: true,
                [`ui-box__addon--${direction}`]: true,
                'ui-box__addon--divided': Boolean(divided),
                [`ui-box__addon--divided-${divided}`]: typeof divided === 'string',
            })}>
            {children}
        </div>
    );
}

function Footer({ children, divided = false, className = '' }: BoxFooterProps) {
    return (
        <div
            className={classNames({
                'ui-box__footer': true,
                'ui-box__footer--divided': divided,
                [className]: Boolean(className),
            })}>
            {children}
        </div>
    );
}

function Overline({ children }: BoxOverlineProps) {
    return <div className='ui-box__overline'>{children}</div>;
}

Title.displayName = 'Box.Title';
Actions.displayName = 'Box.Actions';
Filters.displayName = 'Box.Filters';
AddOn.displayName = 'Box.AddOn';
Footer.displayName = 'Box.Footer';
Overline.displayName = 'Box.Overline';
Box.displayName = 'Box';

Title.slot = 'title';
Actions.slot = 'actions';
Filters.slot = 'filters';
Footer.slot = 'footer';
Overline.slot = 'overline';

Box.Title = Title;
Box.Actions = Actions;
Box.Filters = Filters;
Box.AddOn = AddOn;
Box.Footer = Footer;
Box.Overline = Overline;

export default Box;
